import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDate } from "@/components/badges";
import { ArrowLeft, ChartLine, ShieldAlert } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useTranslation } from "react-i18next";

const mergeSeries = (planned, actual) => {
  const map = {};
  (planned || []).forEach((p) => { map[p.date] = { ...(map[p.date] || { date: p.date }), planned: p.planned }; });
  (actual || []).forEach((a) => { map[a.date] = { ...(map[a.date] || { date: a.date }), actual: a.actual }; });
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
};

const CHART_MARGIN = { top: 8, right: 16, bottom: 4, left: -12 };
const TICK_STYLE = { fontSize: 11 };
const ACTUAL_DOT = { r: 2.5 };

function SCurve({ title, planned, actual, testId }) {
  const { t } = useTranslation();
  const series = useMemo(() => mergeSeries(planned, actual), [planned, actual]);
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        {series.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500" data-testid={`${testId}-empty`}>{t("progressChart.noData")}</p>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={TICK_STYLE} tickFormatter={(d) => d.slice(5)} />
                <YAxis domain={[0, 100]} tick={TICK_STYLE} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v, name) => [`${v}%`, name === "planned" ? t("dashboard.planned") : t("dashboard.actual")]} labelFormatter={(d) => fmtDate(d)} />
                <Legend formatter={(v) => (v === "planned" ? t("dashboard.planned") : t("dashboard.actual"))} />
                <Line type="monotone" dataKey="planned" stroke="#94a3b8" strokeDasharray="6 4" strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="actual" stroke="#2db9f0" strokeWidth={2.5} dot={ACTUAL_DOT} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ProgressChart() {
  const { selectedId } = useProject();
  const { t } = useTranslation();
  const [drill, setDrill] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["progress-chart", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/progress-chart`)).data,
    enabled: !!selectedId,
    retry: false,
  });
  const { data: drillData } = useQuery({
    queryKey: ["progress-chart", selectedId, drill?.party_id],
    queryFn: async () => (await api.get(`/projects/${selectedId}/progress-chart`, { params: { party_id: drill.party_id } })).data,
    enabled: !!selectedId && !!drill,
  });

  const forbidden = error?.response?.status === 403;

  if (forbidden) {
    return (
      <div className="flex flex-col items-center gap-3 py-24 text-center" data-testid="progress-chart-forbidden">
        <ShieldAlert className="h-10 w-10 text-slate-300" />
        <h1 className="font-display text-xl font-bold">{t("progressChart.restrictedTitle")}</h1>
        <p className="max-w-md text-sm text-slate-500">{t("progressChart.restrictedBody")}</p>
      </div>
    );
  }

  if (isLoading || !data) {
    return <div className="space-y-4" data-testid="progress-chart-loading"><Skeleton className="h-72 w-full" /><Skeleton className="h-48 w-full" /></div>;
  }

  if (drill) {
    return (
      <div className="space-y-6" data-testid="progress-chart-drill">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setDrill(null)} data-testid="drill-back-button"><ArrowLeft className="mr-1.5 h-4 w-4" /> {t("progressChart.allCompanies")}</Button>
          <h1 className="font-display text-2xl font-bold tracking-tight">{drill.company_name}</h1>
        </div>
        <SCurve title={t("progressChart.plannedVsActual", { company: drill.company_name })} planned={drillData?.company_planned || []} actual={drillData?.company_actual || []} testId="company-scurve" />
        <Card data-testid="company-activities-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">{t("progressChart.activities")}</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader><TableRow><TableHead>{t("progressChart.thActivity")}</TableHead><TableHead className="w-24">{t("progressChart.thWeight")}</TableHead><TableHead>{t("progressChart.thTargetDate")}</TableHead><TableHead className="w-56">{t("progress.thProgress")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {(drillData?.company_activities || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-sm text-slate-500">{t("progressChart.noActivities")}</TableCell></TableRow>}
                {(drillData?.company_activities || []).map((a) => (
                  <TableRow key={a.id} data-testid={`drill-activity-${a.id}`}>
                    <TableCell className="font-medium">{a.title}</TableCell>
                    <TableCell className="font-mono text-sm font-semibold">{a.weight}%</TableCell>
                    <TableCell className="font-mono text-xs">{a.planned_date || "—"}</TableCell>
                    <TableCell><div className="flex items-center gap-2"><ProgressBar value={a.progress} className="h-1.5 flex-1" /><span className="font-mono text-xs">{a.progress}%</span></div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="progress-chart-page">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight"><ChartLine className="h-6 w-6 text-sky-600" /> {t("progressChart.title")}</h1>
        <p className="text-sm text-slate-500">{t("progressChart.subtitle")}</p>
      </div>

      <SCurve title={t("progressChart.overallTitle")} planned={data.planned} actual={data.actual} testId="overall-scurve" />

      <Card data-testid="company-breakdown-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">{t("progressChart.breakdown")}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader><TableRow><TableHead>{t("admin.thCompany")}</TableHead><TableHead>{t("admin.thRole")}</TableHead><TableHead className="w-28">{t("progressChart.thTotalWeight")}</TableHead><TableHead className="w-64">{t("progress.thProgress")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.companies.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-sm text-slate-500">{t("progressChart.noItems")}</TableCell></TableRow>}
              {data.companies.map((c) => (
                <TableRow key={c.party_id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDrill(c)} data-testid={`company-row-${c.party_id}`}>
                  <TableCell className="font-medium text-sky-700">{c.company_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{c.role}</TableCell>
                  <TableCell className="font-mono text-sm">{c.weight}</TableCell>
                  <TableCell><div className="flex items-center gap-2"><ProgressBar value={c.progress} className="h-1.5 flex-1" /><span className="font-mono text-xs">{c.progress}%</span></div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
